import type {
  Coaching,
  CoachingConfig,
  CoachingPartnership,
  CoachingPlan,
  CoachingSession,
  Cohort,
} from '@efdigital/ll-addon-data';
import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { findCoachingSession } from '@efdigital/ll-admin-v2/data/builders/coaching/coaching-sessions';
import { queryCoachProfileByUserId } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { queryCohortByCoachingId } from '@efdigital/ll-admin-v2/data/builders/xceed/cohorts';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

type Params = { session_id: string };

export type CoachingSessionsSessionModel = {
  coachingSession: CoachingSession;
  coachingPlan: CoachingPlan;
  coaching: Coaching;
  coachingPartnership: CoachingPartnership;
  coachingConfig: CoachingConfig;
  coachProfile?: CoachProfile;
  cohort: Cohort;
  cohortProgramId?: string;
};

export default class CoachingSessionsSessionRoute extends Route<CoachingSessionsSessionModel> {
  @service declare store: Store;

  async model(params: Params) {
    const coachingSession = (
      await this.store.request(findCoachingSession(params.session_id))
    )?.content.data;
    if (!coachingSession) {
      throw new Error('Coaching session not found');
    }

    const coachProfile = (
      await this.store.request(
        queryCoachProfileByUserId(coachingSession.coachUserId, 'published')
      )
    )?.content.data[0];

    const coachingPlan = await coachingSession.coachingPlan;
    if (!coachingPlan) {
      throw new Error('Coaching plan not found');
    }
    const coaching = await coachingPlan.coaching;
    if (!coaching) {
      throw new Error('Coaching not found');
    }
    const coachingPartnership = await coachingSession.coachingPartnership;
    if (!coachingPartnership) {
      throw new Error('Coaching partnership not found');
    }
    const coachingConfig = await coaching.coachingConfig;
    if (!coachingConfig) {
      throw new Error('Coaching config not found');
    }

    const cohort = (
      await this.store.request(
        queryCohortByCoachingId(coaching.id!, ['program'])
      )
    )?.content.data[0];
    if (!cohort) {
      throw new Error('Cohort not found');
    }

    const program = await cohort.program;
    const cohortProgramId = program?.id ?? undefined;

    return {
      coachingSession,
      coachProfile,
      coachingPlan,
      coaching,
      coachingPartnership,
      coachingConfig,
      cohort,
      cohortProgramId,
    };
  }
}
